import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number} */
  #requestSequence = 0;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];
  /** @type {boolean} */
  #ringFlowInitialized = false;
  /** @type {'metal' | 'carat' | 'clarity' | null} */
  #pendingRingAutoOpenStep = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  /**
   * Parses a carat value from a label like "1 ct", "2.5ct", "3 carat".
   * @param {string} value
   * @returns {number | null}
   */
  #parseCaratValue(value) {
    if (typeof value !== 'string') return null;

    const normalized = value.toLowerCase().replace(',', '.');
    const match = normalized.match(/(\d+(?:\.\d+)?)\s*(?:ct|carat)\b/);
    const fallbackMatch = normalized.match(/(\d+(?:\.\d+)?)/);
    const raw = match?.[1] ?? fallbackMatch?.[1];
    if (!raw) return null;

    const parsed = Number.parseFloat(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) return null;
    return parsed;
  }

  /**
   * Converts carat weight to an approximate face-up diameter ratio.
   * Round-diamond diameter scales with cube root of carat weight.
   * @param {number | null} caratValue
   * @returns {number}
   */
  #getCaratDiameterRatio(caratValue) {
    if (typeof caratValue !== 'number' || !Number.isFinite(caratValue) || caratValue <= 0) return 1;
    return Math.cbrt(caratValue);
  }

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('toggle', this.#handleDetailsToggle, { capture: true });
    this.initializeCaratSliders();
    this.#resizeObserver.observe(this);
    this.initializeRingStepFlow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('toggle', this.#handleDetailsToggle, { capture: true });
    this.#resizeObserver.disconnect();
  }

  #handleDetailsToggle = (event) => {
    if (!(event.target instanceof HTMLDetailsElement)) return;
    if (!event.target.matches('details[data-ring-step]')) return;
    this.updateRingSectionSelectedValues();
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target instanceof HTMLInputElement && event.target.matches('[data-carat-slider-input]')) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.initializeRingStepFlow({ sourceTarget: event.target });
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  initializeCaratSliders() {
    const caratFieldsets = Array.from(this.querySelectorAll('fieldset[data-carat-slider-enabled="true"]'));
    if (!caratFieldsets.length) return;

    for (const fieldset of caratFieldsets) {
      if (!(fieldset instanceof HTMLFieldSetElement)) continue;

      const sliderInput = fieldset.querySelector('[data-carat-slider-input]');
      const sliderValue = fieldset.querySelector('[data-carat-slider-value]');
      const sliderPrice = fieldset.querySelector('[data-carat-slider-price]');
      const sliderMin = fieldset.querySelector('[data-carat-slider-min]');
      const sliderMax = fieldset.querySelector('[data-carat-slider-max]');
      const sliderScale = fieldset.querySelector('[data-carat-slider-scale]');
      const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      if (!(sliderInput instanceof HTMLInputElement) || !radios.length) continue;
      const sliderAlreadyBound = sliderInput.dataset.caratSliderBound === 'true';

      const orderedRadios = radios
        .map((radio, originalIndex) => ({
          radio,
          originalIndex,
          order: Number.parseInt(radio.dataset.ringCaratOrder || '', 10),
          caratValue: this.#parseCaratValue(radio.dataset.ringOptionLabel || radio.value || ''),
        }))
        .sort((a, b) => {
          const aCarat = a.caratValue;
          const bCarat = b.caratValue;
          if (typeof aCarat === 'number' && typeof bCarat === 'number') return aCarat - bCarat;
          if (typeof aCarat === 'number') return -1;
          if (typeof bCarat === 'number') return 1;

          const aOrder = Number.isNaN(a.order) ? Number.MAX_SAFE_INTEGER : a.order;
          const bOrder = Number.isNaN(b.order) ? Number.MAX_SAFE_INTEGER : b.order;
          return aOrder - bOrder;
        });

      sliderInput.min = '0';
      sliderInput.max = `${Math.max(orderedRadios.length - 1, 0)}`;
      sliderInput.step = '1';

      const checkedOriginalIndex = radios.findIndex((radio) => radio.checked);
      const checkedOrderedIndex = Math.max(
        0,
        orderedRadios.findIndex((item) => item.originalIndex === checkedOriginalIndex)
      );

      sliderInput.value = `${checkedOrderedIndex}`;

      const syncSliderLabel = (orderedIndex) => {
        const boundedIndex = Math.min(Math.max(orderedIndex, 0), orderedRadios.length - 1);
        const activeRadio = orderedRadios[boundedIndex]?.radio;
        if (activeRadio && sliderValue instanceof HTMLElement) {
          sliderValue.textContent = activeRadio.dataset.ringOptionLabel?.trim() || activeRadio.value;
        }
        if (activeRadio && sliderPrice instanceof HTMLElement) {
          sliderPrice.textContent = activeRadio.dataset.ringOptionPrice?.trim() || '';
          sliderPrice.hidden = sliderPrice.textContent === '';
        }

        if (sliderScale instanceof HTMLElement) {
          const scaleItems = Array.from(sliderScale.querySelectorAll('[data-carat-scale-item]'));
          scaleItems.forEach((item, itemIndex) => {
            if (!(item instanceof HTMLElement)) return;
            item.dataset.active = itemIndex === boundedIndex ? 'true' : 'false';
          });
        }
      };

      if (sliderMin instanceof HTMLElement) {
        sliderMin.textContent = orderedRadios[0]?.radio.dataset.ringOptionLabel?.trim() || orderedRadios[0]?.radio.value || '';
      }
      if (sliderMax instanceof HTMLElement) {
        const lastRadio = orderedRadios[orderedRadios.length - 1]?.radio;
        sliderMax.textContent = lastRadio?.dataset.ringOptionLabel?.trim() || lastRadio?.value || '';
      }

      if (sliderScale instanceof HTMLElement && sliderScale.dataset.caratScaleBuilt !== 'true') {
        sliderScale.innerHTML = '';
        const scaleImageSrc = sliderScale.dataset.caratScaleImageSrc || '';
        sliderScale.style.setProperty('--carat-scale-count', `${orderedRadios.length}`);
        orderedRadios.forEach((item, itemIndex) => {
          const scaleItem = document.createElement('span');
          scaleItem.className = 'ring-carat-slider__scale-item';
          scaleItem.dataset.caratScaleItem = 'true';
          const diameterRatio = this.#getCaratDiameterRatio(item.caratValue);
          scaleItem.style.setProperty('--carat-scale-diameter-ratio', `${diameterRatio}`);
          const position = orderedRadios.length > 1 ? itemIndex / (orderedRadios.length - 1) : 0;
          scaleItem.style.setProperty('--carat-scale-position', `${position}`);

          const scaleImage = document.createElement('img');
          scaleImage.className = 'ring-carat-slider__scale-image';
          scaleImage.src = scaleImageSrc;
          scaleImage.alt = '';
          scaleImage.loading = 'lazy';
          scaleImage.width = 64;
          scaleImage.height = 64;

          scaleItem.appendChild(scaleImage);
          sliderScale.appendChild(scaleItem);
        });
        sliderScale.dataset.caratScaleBuilt = 'true';
      }

      syncSliderLabel(checkedOrderedIndex);

      if (!sliderAlreadyBound) {
        sliderInput.addEventListener('input', () => {
          const orderedIndex = Number.parseInt(sliderInput.value || '0', 10);
          syncSliderLabel(Number.isNaN(orderedIndex) ? 0 : orderedIndex);
        });

        sliderInput.addEventListener('change', () => {
          const orderedIndex = Number.parseInt(sliderInput.value || '0', 10);
          const boundedIndex = Number.isNaN(orderedIndex)
            ? 0
            : Math.min(Math.max(orderedIndex, 0), orderedRadios.length - 1);

          const targetRadio = orderedRadios[boundedIndex]?.radio;
          if (!(targetRadio instanceof HTMLInputElement)) return;
          if (targetRadio.checked) return;

          targetRadio.checked = true;
          targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
        });

        fieldset.addEventListener('change', (event) => {
          const target = event.target;
          if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;

          const orderedIndex = orderedRadios.findIndex((item) => item.radio === target);
          if (orderedIndex < 0) return;
          sliderInput.value = `${orderedIndex}`;
          syncSliderLabel(orderedIndex);
        });

        sliderInput.dataset.caratSliderBound = 'true';
      }
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
      this.updateRingOptionSummaries();
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
      this.updateRingOptionSummaries();
    }
  }

  updateRingOptionSummaries() {
    // Ring picker no longer renders selection summary text.
  }

  /**
   * @param {{ sourceTarget?: EventTarget | null, forceMetalOpen?: boolean }} [options]
   */
  initializeRingStepFlow(options = {}) {
    const sections = Array.from(this.querySelectorAll('details[data-ring-step]'));
    if (!sections.length) return;

    const metalChecked = this.querySelector('fieldset[data-ring-option-kind="metal"] input:checked');
    const caratChecked = this.querySelector('fieldset[data-ring-option-kind="carat"] input:checked');
    const clarityChecked = this.querySelector('fieldset[data-ring-option-kind="clarity"] input:checked');

    const changedKind =
      options.sourceTarget instanceof HTMLElement
        ? options.sourceTarget.closest('fieldset[data-ring-option-kind]')?.dataset.ringOptionKind || null
        : null;
    const shouldForceMetalOpen = options.forceMetalOpen === true;
    const shouldExpandAllInitially = !this.#ringFlowInitialized;

    let openStep = this.#pendingRingAutoOpenStep;
    this.#pendingRingAutoOpenStep = null;

    if (shouldForceMetalOpen) {
      openStep = 'metal';
    } else if (shouldExpandAllInitially) {
      openStep = null;
      this.#pendingRingAutoOpenStep = null;
    } else if (changedKind === 'metal') {
      openStep = 'carat';
      this.#pendingRingAutoOpenStep = 'carat';
    } else if (changedKind === 'carat') {
      openStep = 'clarity';
      this.#pendingRingAutoOpenStep = 'clarity';
    } else if (changedKind === 'clarity') {
      openStep = null;
      this.#pendingRingAutoOpenStep = null;
    }

    const hasMetalSelection = Boolean(metalChecked);
    const hasCaratSelection = Boolean(caratChecked);

    for (const section of sections) {
      if (!(section instanceof HTMLDetailsElement)) continue;
      const step = section.dataset.ringStep;
      if (!step || step === 'default') continue;

      const isLocked = (step === 'carat' && !hasMetalSelection) || (step === 'clarity' && !hasCaratSelection);
      section.toggleAttribute('data-ring-locked', isLocked);
      section.toggleAttribute('data-ring-unlocked', !isLocked);
      section.setAttribute('aria-disabled', isLocked ? 'true' : 'false');

      if (isLocked) {
        section.open = false;
      } else if (shouldExpandAllInitially) {
        section.open = true;
      } else if (openStep !== null && step === openStep) {
        section.open = true;
      }
    }

    this.#ringFlowInitialized = true;
    this.updateRingSectionSelectedValues();
  }

  updateRingSectionSelectedValues() {
    const sections = Array.from(this.querySelectorAll('details[data-ring-step]'));
    if (!sections.length) return;

    for (const section of sections) {
      if (!(section instanceof HTMLDetailsElement)) continue;
      const step = section.dataset.ringStep;
      if (!step || step === 'default') continue;

      const selectedValueElement = section.querySelector(`[data-ring-selected-value="${step}"]`);
      if (!(selectedValueElement instanceof HTMLElement)) continue;
      const hideWhenExpanded = selectedValueElement.dataset.hideSelectedSummary === 'true';

      const fieldset = section.querySelector(`fieldset[data-ring-option-kind="${step}"]`);
      const checkedInput =
        fieldset instanceof HTMLFieldSetElement
          ? fieldset.querySelector('input:checked')
          : null;

      const checkedLabel =
        checkedInput instanceof HTMLInputElement
          ? checkedInput.dataset.ringOptionLabel?.trim() || checkedInput.value?.trim() || ''
          : '';

      if (hideWhenExpanded && section.open) {
        selectedValueElement.hidden = true;
        selectedValueElement.textContent = '';
        continue;
      }

      selectedValueElement.textContent = checkedLabel;
      selectedValueElement.hidden = checkedLabel === '';
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    const requestSequence = ++this.#requestSequence;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestSequence !== this.#requestSequence) return;

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.initializeCaratSliders();
    this.updateVariantPickerCss();
    this.initializeRingStepFlow();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
